import { useEffect, useState, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { requireAuth } from "@/lib/auth";
import { AppLayout, PageTransition } from "@/components/layout";
import { Icon } from "@/components/ui/Icon";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/Avatar";
import { NotificationTypeIcon } from "@/components/notifications";
import { ProfileSidePanel } from "@/components/profile";
import { EventDetailSidePanel } from "@/components/events";
import { PostDetailSidePanel } from "@/components/posts";
import { useAuth } from "@/components/providers/AuthProvider";
import {
  useFetchNotificationsQuery,
  useFetchUserProfilesByIdsQuery,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
  type Notification,
} from "@/api/clubsApi";

export const Route = createFileRoute("/notifications/")({
  beforeLoad: requireAuth,
  component: NotificationsPage,
});

interface TargetHandlers {
  openProfile: (userId: string) => void;
  openEvent: (eventId: string) => void;
  openPost: (clubId: string, postId: string) => void;
}

// Helper to parse notification target URLs into side panels or navigation
function navigateToTarget(
  navigate: ReturnType<typeof useNavigate>,
  targetUrl: string,
  handlers: TargetHandlers
) {
  // /clubs/:id/posts/:postId (with optional query params) → open in side panel
  const postMatch = targetUrl.match(/^\/clubs\/([^/]+)\/posts\/([^?]+)/);
  if (postMatch) {
    handlers.openPost(postMatch[1], postMatch[2]);
    return;
  }

  // /clubs/:id/pricing
  const pricingMatch = targetUrl.match(/^\/clubs\/([^/]+)\/pricing/);
  if (pricingMatch) {
    navigate({
      to: "/clubs/$clubId/pricing",
      params: { clubId: pricingMatch[1] },
    });
    return;
  }

  // /clubs/:id (with optional query/search)
  const clubMatch = targetUrl.match(/^\/clubs\/([^/?]+)/);
  if (clubMatch) {
    navigate({
      to: "/clubs/$identifier",
      params: { identifier: clubMatch[1] },
    });
    return;
  }

  // /events/:id → open in side panel
  const eventMatch = targetUrl.match(/^\/events\/([^/?]+)/);
  if (eventMatch) {
    handlers.openEvent(eventMatch[1]);
    return;
  }

  // /profile/:userId → open in side panel
  const profileMatch = targetUrl.match(/^\/profile\/([^/?]+)/);
  if (profileMatch) {
    handlers.openProfile(profileMatch[1]);
    return;
  }

  // Fallback: use window.location for unrecognized patterns
  window.location.href = targetUrl;
}

function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [markAsRead] = useMarkNotificationAsReadMutation();
  const [markAllAsRead] = useMarkAllNotificationsAsReadMutation();

  // Profile side panel state
  const [profilePanelUserId, setProfilePanelUserId] = useState<string | null>(null);
  const [profilePanelOpen, setProfilePanelOpen] = useState(false);

  // Event side panel state
  const [eventPanelId, setEventPanelId] = useState<string | null>(null);
  const [eventPanelOpen, setEventPanelOpen] = useState(false);

  // Post side panel state
  const [postPanelId, setPostPanelId] = useState<string | null>(null);
  const [postPanelClubId, setPostPanelClubId] = useState<string>("");
  const [postPanelOpen, setPostPanelOpen] = useState(false);

  const openProfile = useCallback((userId: string) => {
    setProfilePanelUserId(userId);
    setProfilePanelOpen(true);
  }, []);

  const openEvent = useCallback((eventId: string) => {
    setEventPanelId(eventId);
    setEventPanelOpen(true);
  }, []);

  const openPost = useCallback((clubId: string, postId: string) => {
    setPostPanelClubId(clubId);
    setPostPanelId(postId);
    setPostPanelOpen(true);
  }, []);

  // Fetch 30 days of notifications
  const {
    data: notifications,
    isLoading,
    error,
  } = useFetchNotificationsQuery({ maxAgeDays: 30 });

  // Get unique creator IDs for avatar fetching
  const creatorIds = notifications
    ? [...new Set(notifications.map((n) => n.creator_id).filter(Boolean))]
    : [];

  // Fetch creator profiles for avatars
  const { data: creatorProfiles } = useFetchUserProfilesByIdsQuery(creatorIds, {
    skip: creatorIds.length === 0,
  });

  // Create a map of userId -> profile for quick lookup
  const profileMap = new Map(
    creatorProfiles?.map((p) => [p.id, p]) || []
  );

  // Mark all notifications as read after 1 second on screen
  useEffect(() => {
    const timer = setTimeout(() => {
      markAllAsRead();
    }, 1000);

    return () => clearTimeout(timer);
  }, [markAllAsRead]);

  const handleNotificationClick = async (notification: Notification) => {
    await markAsRead(notification.id);
    // New member notifications → open the member's profile in side panel
    if (notification.notification_type === "new_member" && notification.creator_id) {
      openProfile(notification.creator_id);
      return;
    }
    if (notification.target_url) {
      navigateToTarget(navigate, notification.target_url, { openProfile, openEvent, openPost });
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <AppLayout>
      <PageTransition>
        {/* Content - offset for floating sidebar on desktop */}
        <div className="p-6 lg:pl-20">
          <div className="max-w-2xl mx-auto">
            {/* Page header */}
            <h1 className="text-h5 text-text-primary mb-6">Activity</h1>

            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-text-tertiary border-t-transparent" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <p className="text-body-1 text-text-secondary">
                  Failed to load notifications
                </p>
              </div>
            ) : notifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 mb-4 rounded-full bg-surface-2 flex items-center justify-center">
                  <Icon name="bell-outline" size={32} className="text-text-tertiary" />
                </div>
                <p className="text-body-1 text-text-secondary">No notifications</p>
                <p className="text-body-2 text-text-tertiary mt-2">
                  You'll see activity from your clubs here
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {notifications?.map((notification) => {
                  const creatorProfile = profileMap.get(notification.creator_id);
                  const avatarUrl = creatorProfile?.avatar_small;
                  const creatorName =
                    creatorProfile?.display_name ||
                    creatorProfile?.first_name ||
                    "";

                  const content = (
                    <div className="group">
                      <div className="flex flex-col gap-1">
                        {/* Icons row: NotificationTypeIcon, Avatar, and red dot */}
                        <div className="flex items-center gap-1.5">
                          <NotificationTypeIcon notification={notification} size={32} />
                          <Avatar className="w-8 h-8">
                            {avatarUrl ? (
                              <AvatarImage src={avatarUrl} alt={creatorName} />
                            ) : null}
                            <AvatarFallback
                              className={`text-xs ${creatorName ? "bg-core-yellow text-core-deepSlate" : ""}`}
                            >
                              {creatorName ? getInitials(creatorName) : ""}
                            </AvatarFallback>
                          </Avatar>
                          {!notification.is_read && (
                            <div className="w-2 h-2 rounded-full bg-core-red" />
                          )}
                        </div>

                        {/* Text content - indented to align with Avatar (32px icon + 6px gap = 38px) */}
                        <div className="ml-[38px] flex flex-col gap-1">
                          <p className="text-body-2-medium text-text-primary">
                            {notification.title}
                          </p>
                          <p className="text-body-2 text-text-secondary">
                            {notification.body}
                          </p>
                          <p className="text-body-3 text-text-tertiary">
                            {DateTime.fromISO(notification.created_at).toRelative()}
                          </p>
                        </div>
                      </div>
                    </div>
                  );

                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`hover:bg-surface-1 -mx-4 px-4 py-2 rounded-xl transition-colors${notification.target_url || notification.notification_type === "new_member" ? " cursor-pointer" : ""}`}
                      role={notification.target_url || notification.notification_type === "new_member" ? "link" : undefined}
                    >
                      {content}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </PageTransition>

      <ProfileSidePanel
        userId={profilePanelUserId}
        open={profilePanelOpen}
        onOpenChange={setProfilePanelOpen}
      />

      {eventPanelId && (
        <EventDetailSidePanel
          eventId={eventPanelId}
          open={eventPanelOpen}
          onOpenChange={setEventPanelOpen}
        />
      )}

      {postPanelId && (
        <PostDetailSidePanel
          postId={postPanelId}
          clubId={postPanelClubId}
          currentUserId={user?.id}
          open={postPanelOpen}
          onOpenChange={setPostPanelOpen}
        />
      )}
    </AppLayout>
  );
}
